import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatApiError = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
      setError(null);
    } catch {
      setUser(null);
      localStorage.removeItem('access_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password }
      );
      const data = response.data;
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      setUser(data);
      return { success: true };
    } catch (err) {
      let errorMsg;
      if (err.response) {
        errorMsg = formatApiError(err.response.data?.detail) || `Server error: ${err.response.status}`;
      } else if (err.request) {
        errorMsg = "Cannot reach server. Please check your connection.";
      } else {
        errorMsg = err.message || "Something went wrong";
      }
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const register = async (name, email, password) => {
    try {
      setError(null);
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        { name, email, password }
      );
      const data = response.data;
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      setUser(data);
      return { success: true };
    } catch (err) {
      const errorMsg = formatApiError(err.response?.data?.detail) || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        headers: getAuthHeaders(),
      });
    } catch {
      // Logout error silently handled
    } finally {
      localStorage.removeItem('access_token');
      setUser(null);
    }
  };

  const forgotPassword = async (email) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      return { success: true, message: response.data.message, reset_token: response.data.reset_token };
    } catch (err) {
      const errorMsg = formatApiError(err.response?.data?.detail) || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      setError(null);
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: password });
      return { success: true };
    } catch (err) {
      const errorMsg = formatApiError(err.response?.data?.detail) || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const acceptInvitation = async (token, name, password) => {
    try {
      setError(null);
      const response = await axios.post(
        `${API_URL}/api/auth/accept-invitation`,
        { token, name, password }
      );
      const data = response.data;
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      setUser(data);
      return { success: true };
    } catch (err) {
      const errorMsg = formatApiError(err.response?.data?.detail) || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Role-based permission check
  const hasPermission = useCallback((permission, channelId = null) => {
    if (!user) return false;
    const role = user.role || 'viewer';
    
    // Owner and admin have all permissions
    if (role === 'owner' || role === 'admin') return true;
    
    // Manager can edit videos on assigned channels but NOT create/delete
    if (role === 'manager') {
      const assignedChannels = user.assigned_channels;
      if (!assignedChannels || assignedChannels.includes(channelId)) {
        return ['edit_videos', 'view_videos'].includes(permission);
      }
      return false;
    }
    
    // Editor can edit their own assigned videos
    if (role === 'editor') {
      return ['edit_videos', 'view_videos'].includes(permission);
    }
    
    // Viewer can only view
    if (role === 'viewer') {
      return permission === 'view_videos';
    }
    
    return false;
  }, [user]);

  // Check if user can access a specific channel
  const canAccessChannel = useCallback((channelId) => {
    if (!user) return false;
    const role = user.role || 'viewer';
    
    // Owner and admin can access all channels
    if (role === 'owner' || role === 'admin') return true;
    
    // Others check assigned_channels
    const assignedChannels = user.assigned_channels;
    if (!assignedChannels) return true; // null means all channels
    return assignedChannels.includes(channelId);
  }, [user]);

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    error,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    acceptInvitation,
    checkAuth,
    setError,
    getAuthHeaders,
    hasPermission,
    canAccessChannel,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
